import { Decimal } from 'decimal.js';
import {
  DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT,
  MAX_RATE_DISPLAY_DECIMAL_LIMIT,
  STABLECOIN_MAX_RATE_DISPLAY_DECIMAL_LIMIT,
  STABLECOIN_RATE_CURRENCIES,
  STABLECOIN_RATE_FIAT_ANCHOR_CURRENCY,
} from '@/shared/common/constants/rateDisplayPrecision.constant';
import type {
  DisplayedRateDetailsInput,
  DisplayedRateDetailsValues,
  StablecoinBreakdownDecimalsInput,
} from '@/shared/common/types/rateDetails.type';

/**
 * Indica si el código de moneda participa en la precisión adaptativa de
 * stablecoins (ver `getStablecoinAwareRateDecimals`).
 * @param currency - Código de moneda a evaluar.
 * @returns true si la moneda es USDT o USDC.
 */
export function isStablecoinCurrency(currency?: string | null): boolean {
  if (!currency) {
    return false;
  }

  return STABLECOIN_RATE_CURRENCIES.has(currency);
}

/**
 * Indica si el par de monedas debe usar la precisión adaptativa de stablecoins.
 * Solo aplica cuando ambos lados son stablecoins (USDT<->USDC, ~1:1) o cuando
 * un stablecoin va contra USD (también ~1:1). Un stablecoin contra cualquier
 * otra moneda fiat (CLP, COP, ARS...) ya tiene una diferencia de escala grande
 * entre origen y destino, así que el spread es visible con la precisión
 * dinámica normal y no necesita la adaptativa.
 * @param originCurrency - Moneda origen del par.
 * @param destinationCurrency - Moneda destino del par.
 * @returns true si el par es stablecoin<->stablecoin o stablecoin<->USD.
 */
export function isStablecoinRatePair(
  originCurrency?: string | null,
  destinationCurrency?: string | null
): boolean {
  const isOriginStablecoin = isStablecoinCurrency(originCurrency);
  const isDestinationStablecoin = isStablecoinCurrency(destinationCurrency);

  if (isOriginStablecoin && isDestinationStablecoin) {
    return true;
  }

  if (isOriginStablecoin) {
    return destinationCurrency === STABLECOIN_RATE_FIAT_ANCHOR_CURRENCY;
  }

  if (isDestinationStablecoin) {
    return originCurrency === STABLECOIN_RATE_FIAT_ANCHOR_CURRENCY;
  }

  return false;
}

/**
 * Cuenta los decimales reales de un valor, sin imponer un límite fijo.
 * @param value - Número a inspeccionar.
 * @returns Cantidad de decimales de `value`, o 0 si no es un número finito.
 */
function getRawDecimalPlaces(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return new Decimal(value).decimalPlaces();
}

/**
 * Expone los decimales reales de una tasa en pares USD<->stablecoin, acotados
 * por `STABLECOIN_MAX_RATE_DISPLAY_DECIMAL_LIMIT`. `value` debe venir del
 * backend: un valor calculado en el front arrastra ruido de punto flotante que
 * se contaría como precisión real.
 * @param value - Valor de tasa del backend que se va a formatear.
 * @param originCurrency - Moneda origen del par.
 * @param destinationCurrency - Moneda destino del par.
 * @returns Precisión adaptativa de stablecoins o el default de tasas.
 */
export function getStablecoinAwareRateDecimals(
  value: number,
  originCurrency?: string | null,
  destinationCurrency?: string | null
): number {
  if (!isStablecoinRatePair(originCurrency, destinationCurrency)) {
    return DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT;
  }

  if (hasFloat32RoundingArtifact(value)) {
    return DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT;
  }

  return getBackendRateDecimals(value);
}

/**
 * Detecta si el valor es el resultado de redondear a dos decimales en un float
 * de 32 bits, en cuyo caso los decimales extra son ruido y no precision.
 *
 * `ms-quote` persiste algunas tasas con `Math.round(x * 100F) / 100F`, y esa
 * cuenta en 32 bits llega al front expandida a double: `1.16` viaja como
 * `1.159999966621399` y `3783.28` como `3783.280029296875`. Contar esos digitos
 * como precision del backend hace que la UI renderice el ruido.
 *
 * Solo aplica donde la tasa se renderiza sola. En el desglose la precision es
 * compartida y recortarla rompe la reconciliacion (ver `getBackendRateDecimals`).
 * @param value - Valor de tasa tal cual lo envio el backend.
 * @returns true si los decimales extra son artefacto del float de 32 bits.
 */
function hasFloat32RoundingArtifact(value: number): boolean {
  if (!Number.isFinite(value)) {
    return false;
  }

  const scale = 10 ** DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT;
  const roundedValue = Math.round(value * scale) / scale;

  return Object.is(Math.fround(roundedValue), value);
}

/**
 * Decimales que el backend envio para una tasa, acotados entre el default y el
 * techo de precision. Es el piso de precision del desglose (cotizador y
 * confirmacion), donde la tasa mostrada tiene que reconstruir el monto destino.
 *
 * No descarta el artefacto de float de 32 bits a proposito: Spot, Spread y
 * Global66 comparten una sola precision, y la del Global66 sale de los montos,
 * no del spot. Recortarla a la del spot desarma la reconciliacion — en un
 * EUR -> USD de 1.000 son 1,66 USD de desvio.
 * @param value - Valor de tasa tal cual lo envio el backend.
 * @returns Decimales a mostrar para ese valor.
 */
function getBackendRateDecimals(value: number): number {
  return Math.min(
    Math.max(getRawDecimalPlaces(value), DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT),
    STABLECOIN_MAX_RATE_DISPLAY_DECIMAL_LIMIT
  );
}

function roundDisplayedRate(amount: number, precision: number): number {
  const roundedAmount = new Decimal(amount).toDecimalPlaces(
    precision,
    Decimal.ROUND_HALF_UP
  );

  return roundedAmount.isZero() ? 0 : roundedAmount.toNumber();
}

function getDisplayedSpreadValue(
  displayedSpotRateValue: number,
  displayedGlobal66RateValue: number,
  precision: number
): number {
  return roundDisplayedRate(
    new Decimal(displayedGlobal66RateValue)
      .minus(displayedSpotRateValue)
      .toNumber(),
    precision
  );
}

/**
 * Busca la menor precisión que hace visible el spread, subiendo de a un decimal
 * hasta que deja de redondear a cero. Es lo que evita mostrar "spread 0.00" en
 * una operación que sí cobra comisión. La búsqueda recorre hasta `maxPrecision`:
 * acotarla a un techo menor haría que un spread visible recién en 7 decimales
 * saltara directo al máximo en vez de usar los 7 que alcanzan.
 * @param rawSpotRateValue - Valor crudo del spot.
 * @param rawGlobal66RateValue - Valor crudo del tipo Global66.
 * @param maxPrecision - Techo de la búsqueda.
 * @returns La precisión mínima que revela el spread, o `maxPrecision`.
 */
function getRateDetailPrecision(
  rawSpotRateValue: number,
  rawGlobal66RateValue: number,
  maxPrecision: number
): number {
  const rawSpreadRateValue = new Decimal(rawGlobal66RateValue).minus(
    rawSpotRateValue
  );

  if (rawSpreadRateValue.isZero()) {
    return DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT;
  }

  for (
    let precision = DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT;
    precision < maxPrecision;
    precision += 1
  ) {
    const displayedSpotRateValue = roundDisplayedRate(
      rawSpotRateValue,
      precision
    );
    const displayedGlobal66RateValue = roundDisplayedRate(
      rawGlobal66RateValue,
      precision
    );
    const displayedSpreadRateValue = getDisplayedSpreadValue(
      displayedSpotRateValue,
      displayedGlobal66RateValue,
      precision
    );

    if (displayedSpreadRateValue !== 0) {
      return precision;
    }
  }

  return maxPrecision;
}

/**
 * Decimales de las tres filas del desglose en un par USD<->stablecoin: los que
 * realmente mandó el backend, pero nunca menos de los que hacen falta para que
 * el spread deje de redondear a cero. Sin ese piso, un backend que manda la
 * tasa "redonda" (ej. `1`) haría mostrar Spot = Global66 y spread 0.00 en una
 * operación que sí cobra comisión.
 * @param params - Tasas del backend, valores crudos del desglose y el par.
 * @returns Precisión compartida por Spot / Spread / Global66.
 */
export function getStablecoinBreakdownDecimals({
  backendRateValues,
  rawSpotRateValue,
  rawGlobal66RateValue,
  originCurrency,
  destinationCurrency,
}: StablecoinBreakdownDecimalsInput): number {
  if (!isStablecoinRatePair(originCurrency, destinationCurrency)) {
    return DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT;
  }

  return getBackendBreakdownDecimals({
    backendRateValues,
    rawSpotRateValue,
    rawGlobal66RateValue,
  });
}

/**
 * Decimales del desglose de un par con stablecoin: los que mando el backend,
 * nunca menos de los que hacen falta para que el spread deje de redondear a
 * cero. **Sus dos llamadores gatean el par antes de entrar** — no comprueba
 * `isStablecoinRatePair` por si misma, asi que llamarla sin ese gate abriria la
 * precision del backend a cualquier par.
 * @param params - Tasas del backend y valores crudos del desglose.
 * @returns Precision compartida por Spot / Spread / Global66.
 */
function getBackendBreakdownDecimals({
  backendRateValues,
  rawSpotRateValue,
  rawGlobal66RateValue,
}: Omit<
  StablecoinBreakdownDecimalsInput,
  'originCurrency' | 'destinationCurrency'
>): number {
  const backendPrecision = backendRateValues.reduce(
    (highestPrecision, backendRateValue) =>
      Math.max(highestPrecision, getBackendRateDecimals(backendRateValue)),
    DEFAULT_RATE_DISPLAY_DECIMAL_LIMIT
  );

  return Math.max(
    backendPrecision,
    getRateDetailPrecision(
      rawSpotRateValue,
      rawGlobal66RateValue,
      STABLECOIN_MAX_RATE_DISPLAY_DECIMAL_LIMIT
    )
  );
}

/**
 * Decide la precisión visible de los detalles de la tasa y devuelve los valores
 * ya redondeados que deben usarse en UI para mantener consistencia entre spot,
 * spread y Global66 rate. Un par stablecoin<->stablecoin o stablecoin<->USD usa
 * los decimales que envió el backend; cualquier otro par busca la menor
 * precisión que revele el spread, hasta `MAX_RATE_DISPLAY_DECIMAL_LIMIT`.
 * @param params - Valores crudos del spot y del tipo Global66, con monedas
 * del par para detectar precisión de stablecoins.
 * @returns Valores listos para renderizar con la precisión final elegida.
 */
export function getDisplayedRateDetailsValues({
  rawSpotRateValue,
  rawGlobal66RateValue,
  originCurrency,
  destinationCurrency,
}: DisplayedRateDetailsInput): DisplayedRateDetailsValues {
  // Solo el spot llega del backend; el Global66 se arma sumandole el spread en
  // el front, asi que su ruido de punto flotante no puede definir la precision.
  const precision = isStablecoinRatePair(originCurrency, destinationCurrency)
    ? getBackendBreakdownDecimals({
        backendRateValues: [rawSpotRateValue],
        rawSpotRateValue,
        rawGlobal66RateValue,
      })
    : getRateDetailPrecision(
        rawSpotRateValue,
        rawGlobal66RateValue,
        MAX_RATE_DISPLAY_DECIMAL_LIMIT
      );
  const displayedSpotRateValue = roundDisplayedRate(rawSpotRateValue, precision);
  const displayedGlobal66RateValue = roundDisplayedRate(
    rawGlobal66RateValue,
    precision
  );
  const displayedSpreadRateValue = getDisplayedSpreadValue(
    displayedSpotRateValue,
    displayedGlobal66RateValue,
    precision
  );
  // El signo no importa: en la orientacion originToDestination <= 1 el costo
  // se resta del lado destino visible (ver getSpreadPerDestinationValue), asi
  // que Global66 < Spot es el resultado esperado de convertir de la moneda
  // fuerte a la debil, no un error a esconder. La magnitud si importa: sin
  // costo FX (o con montos degenerados) el spread da 0 y ahi no hay nada que
  // desglosar.
  const shouldShowBreakdownDetails = displayedSpreadRateValue !== 0;

  return {
    precision,
    displayedSpotRateValue,
    displayedGlobal66RateValue,
    displayedSpreadRateValue,
    shouldShowBreakdownDetails,
  };
}
